import * as _ from 'lodash';
import { StepAction } from './stepAction';
import { getLogger } from '../../commons/logger';
import { getVisualTestingResultHandler } from '../utils/visualTestingResultHandler';
import { getActiveVisualTestingProvider } from '../providers/VisualTestingProviderFactory';
import type { RunContext } from './stepAction';
import type { ExtendedContext } from 'clickim/src/background/eyesUtils';
import type { SimpleUIVerificationStep } from 'clickim/src/common/models/simpleUiVerificationStep';
import type { ExecutionResult } from 'clickim/src/background/stepActions/pixelValidationStepAction';
import type { ScreenshotSettings } from '../providers/IVisualTestingProvider';

const logger = getLogger('pixel-validation-step-action');

export class PixelValidationStepAction extends StepAction<SimpleUIVerificationStep, ExtendedContext> {
    private runContext!: RunContext;

    override async performAction() {
        this.runContext = this.context.getRunContext(undefined);
        const finalParams = this.runContext.incomingParams?.final || {};

        const { provider, providerType } = getActiveVisualTestingProvider({
            runContext: this.runContext,
            context: this.context,
        });

        const targetElementData = this.getTarget() || {};
        let result: ExecutionResult;

        try {
            const client = this.driver.client;
            if (!client) {
                throw new Error('WebDriver client not available');
            }
            await provider.initialize(client);

            const settings: ScreenshotSettings & { stepId?: string } = {
                fully: this.step.action === 'stitched',
                stepId: this.step.id,
            };

            if (this.step.action === 'element' && targetElementData.seleniumElement) {
                settings.region = targetElementData.seleniumElement;
            }

            const customSettings = finalParams.visualStepSettings || finalParams.applitoolsStepSettings;
            if (_.isPlainObject(customSettings)) {
                Object.assign(settings, customSettings);
            }

            await provider.captureScreenshot(settings);
            const providerResult = await provider.finalize();

            result = {
                provider: providerType,
                success: providerResult.success,
                eyesResults: providerResult.eyesResults || [],
                err: providerResult.err,
            } as ExecutionResult;
        } catch (err) {
            logger.error('Visual validation step failed', {
                error: err instanceof Error ? err.message : String(err),
                provider: providerType,
            });
            result = {
                provider: providerType,
                success: false,
                err,
            } as ExecutionResult;
        }

        const handleResult = getVisualTestingResultHandler();
        return await handleResult(this.context, result, this.step as unknown as SimpleUIVerificationStep);
    }
}
